import { useState } from 'react'
import { Button } from '../Common/Button'
import { Textarea } from '../Common'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded'
import type { ChecklistItem } from '../../types/schema'
import {
  approveChecklistProof,
  rejectChecklistProof,
  type ProofReviewer,
} from '../../services/checklistProofReviewService'

interface ProofReviewActionsProps {
  item: Pick<
    ChecklistItem,
    | 'id'
    | 'workspaceId'
    | 'title'
    | 'previousWorkflowStatus'
    | 'parentContainerId'
    | 'proofApproverId'
    | 'proofFileUrl'
    | 'proofSubmissionId'
    | 'proofReviewStatus'
    | 'workflowStatus'
    | 'proofUploadedByName'
    | 'proofRejectionNote'
  > & Partial<Pick<ChecklistItem, 'checklistType' | 'proofPeriodKey' | 'boardStatus'>>
  pageKey?: string | null
  actor: ProofReviewer | null
  onReviewed?: () => void
  compact?: boolean
}

const STEPS = ['Submitted', 'Under Review', 'Approved'] as const

/**
 * Shared 3-step ribbon + Approve / Reject controls. Rendered on every
 * surface that shows a proof-review queue (console filter chip, member
 * board review inspector, helper approval strip). Visible only when the
 * viewer is the item's designated `proofApproverId`.
 */
export const ProofReviewActions = ({ item, pageKey, actor, onReviewed, compact = false }: ProofReviewActionsProps) => {
  const [note, setNote] = useState('')
  const [showNoteSheet, setShowNoteSheet] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasProof = Boolean(item.proofFileUrl || item.proofSubmissionId)
  const isApprover = actor && item.proofApproverId && actor.uid === item.proofApproverId
  const reviewStatus = item.proofReviewStatus ?? null
  const activeStep = reviewStatus === 'APPROVED'
    ? 2
    : reviewStatus === 'REJECTED'
      ? 0
      : hasProof ? 1 : 0

  if (!item.proofApproverId || !hasProof) return null

  const handleApprove = async () => {
    if (!isApprover || !actor) return
    setBusy(true)
    setError(null)
    try {
      await approveChecklistProof({ item, approver: actor, pageKey })
      onReviewed?.()
    } catch (err) {
      setError((err as Error)?.message ?? 'Approval failed.')
    } finally {
      setBusy(false)
    }
  }

  const handleReject = async () => {
    if (!isApprover || !actor) return
    if (!note.trim()) {
      setError('Please add a rejection note so the assignee knows what to fix.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await rejectChecklistProof({ item, approver: actor, note, pageKey })
      setNote('')
      setShowNoteSheet(false)
      onReviewed?.()
    } catch (err) {
      setError((err as Error)?.message ?? 'Rejection failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`proofReviewShell ${compact ? 'isCompact' : ''}`} data-testid="proof-review-actions">
      <ol className="proofReviewRibbon" aria-label="Proof review progress">
        {STEPS.map((label, index) => {
          const state = index < activeStep ? 'done' : index === activeStep ? 'active' : 'pending'
          const Icon = index === 0 ? HourglassBottomRoundedIcon : index === 1 ? HourglassBottomRoundedIcon : CheckCircleRoundedIcon
          return (
            <li key={label} className={`proofReviewStep proofReviewStep--${state}`}>
              <span className="proofReviewStepDot"><Icon fontSize="inherit" /></span>
              <span className="proofReviewStepLabel">{label}</span>
            </li>
          )
        })}
      </ol>

      {reviewStatus === 'APPROVED' && (
        <p className="proofReviewNotice proofReviewNotice--approved">
          <CheckCircleRoundedIcon fontSize="small" /> Approved — item complete.
        </p>
      )}
      {reviewStatus === 'REJECTED' && (
        <p className="proofReviewNotice proofReviewNotice--rejected">
          <CancelRoundedIcon fontSize="small" /> Rejected: {item.proofRejectionNote ?? 'No note provided.'}
        </p>
      )}

      {isApprover && reviewStatus === 'PENDING' && (
        <div className="proofReviewActions">
          {!showNoteSheet ? (
            <>
              <Button
                type="button"
                variant="primary"
                size="sm"
                icon={<CheckCircleRoundedIcon fontSize="small" />}
                disabled={busy}
                onClick={() => void handleApprove()}
              >
                {busy ? 'Approving...' : 'Approve'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                tone="danger"
                icon={<CancelRoundedIcon fontSize="small" />}
                disabled={busy}
                onClick={() => setShowNoteSheet(true)}
              >
                Reject
              </Button>
            </>
          ) : (
            <div className="proofReviewNoteSheet" role="group" aria-label="Rejection note">
              <label>
                <span>Why is this proof insufficient?</span>
                <Textarea
                  value={note}
                  onChange={(next) => setNote(next)}
                  rows={3}
                  placeholder="e.g. Photo is too blurry — retake close to the signage."
                  disabled={busy}
                />
              </label>
              <div className="proofReviewNoteSheetActions">
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  disabled={busy}
                  onClick={() => { setShowNoteSheet(false); setNote(''); setError(null) }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  tone="danger"
                  disabled={busy || !note.trim()}
                  onClick={() => void handleReject()}
                >
                  {busy ? 'Rejecting...' : 'Send rejection'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {error ? <p className="proofReviewError" role="alert">{error}</p> : null}
    </div>
  )
}
